import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Form, Spinner } from 'react-bootstrap';

import { Button, Tooltip } from '@sumologic/yama-ds-components';
import { useTheme } from '@sumologic/yama-ds-components/hooks';

import User from '~/models/User';
import LoginModel from '~/models/Login';

import { toErrorMessage } from '~/modules/helpers/error';

import Icon from '~/components/Icon/Icon';
import Loader from '~/components/Loader/Loader';
import AppLogo from '~/components/AppLogo/AppLogo';
import Translate from '~/components/Translate/Translate';

import permissionDenied from '~/public/img/animatedSVG/permissionDenied';

import Lottie from '~/containers/Lottie/Lottie';
import StickyAlertContext from '~/containers/StickyAlertManager/StickyAlertContext';

import ForgotPassword from './ForgotPasswordModal/ForgotPassword';

/**
 * Login page
 * @param children
 * @returns {JSX.Element|*}
 * @constructor
 * @author FM
 */
export default function Login({ children }) {
    const currentTheme = useTheme()?.palette?.mode || 'dark';

    /** FM: is login permitted */
    const [loginStatus, setLoginStatus] = useState(false);
    /** FM: is login in progress */
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    /** FM: is sso in progress */
    const [isSSOLoading, setIsSSOLoading] = useState(false);
    /** FM: is page loading */
    const [isPageLoading, setIsPageLoading] = useState(true);
    /** FM: login form data */
    const [data, setData] = useState({ username: '', password: '' });
    /** FM: login form data errors */
    const [formError, setFormError] = useState({
        username: false,
        password: false
    });
    /** RN: forgot password modal show */
    const [showForgotPswModal, setShowForgotPswModal] = useState(false);

    const stickyAlertContext = useContext(StickyAlertContext);

    //FM: in case user is authenticated redirect to app stack
    // useEffect(() => {
    //     if (!isPageLoading || !isLoggingIn) {
    //         if (
    //             !window.location.href.includes('/ui/') &&
    //             (loginStatus || getLoginToken())
    //         ) {
    //             window.location.replace(
    //                 `${APP_URL.django}/ui/${location.hash}`
    //             );
    //         }
    //     }
    // });

    //FM: check login status
    useEffect(() => {
        //FM: show error if present in params uri
        showUrlError();
        const callback = () => {
            setIsPageLoading(false);
        };
        const rejectCallback = () => {
            // FM: remove client token
            window.localStorage.removeItem('sessionid');
            setIsPageLoading(false);
        };

        User.ping()
            .then(() => {
                setLoginStatus(true);
            })
            .catch(exception => {
                console.error(exception);
                setLoginStatus(false);
            });

        if (getLoginToken()) {
            callback();
        } else {
            rejectCallback();
        }
    }, []);

    /**
     * Get URL params 'error' and show sticky
     * @author FM
     */
    const showUrlError = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const error = urlParams.get('error');
        if (error) {
            showStickyMessage(error, true);
        }
    };

    /**
     * Checks whether user is logged in or not
     * @returns {boolean}
     */
    const getLoginToken = () => {
        let output;
        switch (SETTINGS.USER_ROLE_MANAGEMENT) {
            case 'json':
            case 'cip':
                // vo: check if current user has been generated by backend
                output = Object.keys(CURRENT_USER).length !== 0;
                break;
            case 'db':
                // vo: will be set to true after successfully login in
                output = loginStatus;
                break;
            default:
                output = false;
        }
        return output;
    };

    /**
     * Show sticky login error
     * @param message
     * @param isSSO
     * @author FM
     */
    const showStickyMessage = (message, isSSO = false) => {
        stickyAlertContext.addStickyAlert({
            status: 'danger',
            title: <Translate message={isSSO ? 'SSO' : 'Login'} />,
            message: message
        });
    };

    /**
     * Handle login errors
     * @param exception
     */
    const errorHandler = exception => {
        //FM: print console error
        const errors = exception?.response?.data?.detail ?? 'Login error';
        const errorsList = [];
        //FM: show sticky error
        if (typeof errors === 'string' || errors instanceof String) {
            stickyAlertContext.addStickyAlert({
                status: 'danger',
                title: errors || <Translate message="Failed" />,
                message: <Translate message="An error occurred!" />
            });
        } else {
            for (const [key, value] of Object.entries(errors)) {
                if (key === 'detail') {
                    errorsList.push(`${value}`);
                } else if (key === 'password') {
                    setFormError({ ...formError, password: true });
                } else if (key === 'username') {
                    setFormError({ ...formError, username: true });
                } else {
                    errorsList.push(`${key}: ${value}`);
                }
            }
            stickyAlertContext.addStickyAlert({
                status: 'danger',
                title: <Translate message="Failed" />,
                message: errorsList.join('\n') || (
                    <Translate message="An error occurred!" />
                )
            });
        }
        setIsLoggingIn(false);
        setIsSSOLoading(false);
    };

    /**
     * Handle license errors
     * @param e
     * @param title
     */
    const licenseErrorHandler = (e, title) => {
        //FM: print console error
        console.error(e);
        //FM : print sticky error
        stickyAlertContext.addStickyAlert({
            status: 'danger',
            title: <Translate message={title} />,
            message: e.response.data || <Translate message="License expired" />
        });
        setIsLoggingIn(false);
    };

    /**
     * Handle login
     */
    const handleLogin = () => {
        setIsLoggingIn(true);
        LoginModel.login(data.username, data.password)
            .then(() => {
                //FM: correct credentials -> redirect
                window.location.replace(
                    `${APP_URL.django}/ui/${location.hash}`
                );
                setLoginStatus(true);
            })
            .catch(exception => {
                const errStatus = exception.response?.status;
                if (403 === errStatus) {
                    stickyAlertContext.addStickyAlert({
                        status: 'danger',
                        title: <Translate message="Failed" />,
                        message: toErrorMessage(exception)
                    });
                    setIsLoggingIn(false);
                    setIsSSOLoading(false);
                } else if (555 === errStatus) {
                    return licenseErrorHandler(exception, 'License expired');
                } else if (556 === errStatus) {
                    return licenseErrorHandler(
                        exception,
                        'Perpetual version mismatch'
                    );
                } else {
                    errorHandler(exception);
                }
            });
    };

    /**
     * Handle SSO
     */
    const handleSSO = () => {
        setIsSSOLoading(true);
        LoginModel.sso()
            .then(response => {
                setIsSSOLoading(false);
                if (response?.data) {
                    window.location.replace(response.data);
                }
            })
            .catch(exception => {
                errorHandler(exception);
            });
    };

    //FM: check if page is ready to be rendered
    if (isPageLoading) {
        return <Loader />;
    } else if (loginStatus || getLoginToken()) {
        //FM: in case session is active render application router
        // vo: redirect from login page if user is already logged in
        if (window.location.toString().includes('ui/login/')) {
            window.location.replace(`${APP_URL.django}/ui/`);
        }

        return children;
    } else {
        return (
            <>
                {!SETTINGS.USING_ONE_PLATFORM ? (
                    <LoginForm
                        formData={data}
                        setFormData={setData}
                        handleLogin={handleLogin}
                        handleSSO={handleSSO}
                        isLoginLoading={isLoggingIn}
                        isSSOLoading={isSSOLoading}
                        formError={formError}
                        setFormError={setFormError}
                        setShowForgotPswModal={setShowForgotPswModal}
                        currentTheme={currentTheme}
                    />
                ) : (
                    <>
                        <Lottie
                            options={{ animationData: permissionDenied }}
                            width={300}
                            height={250}
                        />
                        <div className={'text-center'}>
                            <Translate
                                message="Access denied.
                                 Please verify that you organization has been given access to Cloud SOAR."
                            />
                        </div>
                    </>
                )}
                {showForgotPswModal && !SETTINGS.USING_ONE_PLATFORM && (
                    <ForgotPassword
                        show={showForgotPswModal}
                        onHide={() => {
                            setShowForgotPswModal(false);
                        }}
                    />
                )}
            </>
        );
    }
}

/**
 * Login form
 * @type {React.NamedExoticComponent<{readonly handleLogin?: *, readonly handleSSO?: *, readonly setFormData?: *, readonly formData?: *}>}
 * @author FM
 */
const LoginForm = React.memo(
    ({
        formData,
        setFormData,
        handleLogin,
        handleSSO,
        isLoginLoading,
        isSSOLoading,
        formError,
        setFormError,
        setShowForgotPswModal,
        currentTheme
    }) => {
        const info = 'Sumo Logic S.p.A. - Copyright 2008-2021';
        return (
            <div id={'login-page'}>
                <div className={'login-container'}>
                    <Tooltip
                        title={<span>{info}</span>}
                        placement="right"
                        arrow
                    >
                        <div className={'info'}>
                            <Icon type="information" />
                        </div>
                    </Tooltip>
                    <div className={'container-logo'}>
                        <AppLogo
                            logoType={'gui'}
                            className={`logo raster mb-3`}
                            maxHeight={'70px'}
                            maxWidth={'220px'}
                            theme={currentTheme}
                        />
                    </div>
                    <Form className={'login-form'}>
                        <Form.Group
                            controlId="username"
                            className={'form-group'}
                        >
                            <Form.Label>
                                <Translate message="Username" />
                            </Form.Label>
                            <Form.Control
                                value={formData.username}
                                autoComplete="off"
                                onKeyDown={event => {
                                    //FM: login on enter
                                    if (13 === event.keyCode) {
                                        handleLogin();
                                    }
                                }}
                                onChange={e => {
                                    setFormData({
                                        ...formData,
                                        username: e.target.value
                                    });
                                    setFormError({
                                        ...formError,
                                        username: false
                                    });
                                }}
                                type="text"
                                isInvalid={formError.username}
                            />
                        </Form.Group>

                        <Form.Group
                            controlId="password"
                            className={'mb-1 form-group'}
                        >
                            <Form.Label>
                                <Translate message="Password" />
                            </Form.Label>
                            <Form.Control
                                value={formData.password}
                                autoComplete="off"
                                onKeyDown={event => {
                                    //FM: login on enter
                                    if (13 === event.keyCode) {
                                        handleLogin();
                                    }
                                }}
                                onChange={e => {
                                    setFormData({
                                        ...formData,
                                        password: e.target.value
                                    });
                                    setFormError({
                                        ...formError,
                                        password: false
                                    });
                                }}
                                type="password"
                                isInvalid={formError.password}
                            />
                        </Form.Group>
                        <div
                            className="forgot-psw"
                            onClick={() => {
                                setShowForgotPswModal(true);
                            }}
                        >
                            <Translate message="Forgot password" />?
                        </div>
                    </Form>
                    <div className={'buttons-container d-grid'}>
                        <Button
                            onClick={handleLogin}
                            variant="contained"
                            color="primary"
                            disabled={!formData.password && !formData.username}
                        >
                            {isLoginLoading ? (
                                <Spinner
                                    variant="dark"
                                    animation="border"
                                    size="sm"
                                />
                            ) : (
                                <Translate message="login" />
                            )}
                        </Button>
                        <Button
                            onClick={handleSSO}
                            variant="outlined"
                            color="primary"
                            className={'mt-2'}
                        >
                            {isSSOLoading ? (
                                <Spinner
                                    variant="dark"
                                    animation="border"
                                    size="sm"
                                />
                            ) : (
                                <Translate message="Sign in using SUMO Logic" />
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
);

LoginForm.propTypes = {
    formData: PropTypes.object.isRequired,
    setFormData: PropTypes.func.isRequired,
    handleLogin: PropTypes.func.isRequired,
    handleSSO: PropTypes.func,
    isLoginLoading: PropTypes.bool,
    isSSOLoading: PropTypes.bool,
    setShowForgotPswModal: PropTypes.func
};
LoginForm.defaultProps = {
    handleSSO: () => {},
    isLoginLoading: false,
    isSSOLoading: false
};
